"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { VIMEO_CONFIG } from "@/lib/config";

interface VimeoBackgroundSquareProps {
  startTime?: number; // Start time in seconds
  className?: string;
}

export function VimeoBackgroundSquare({
  startTime = 0,
  className = "",
}: VimeoBackgroundSquareProps) {
  const [showPoster, setShowPoster] = useState(true);
  const videoId = VIMEO_CONFIG.getBackgroundVideoId();

  // Hide poster after a delay to allow video to start playing
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPoster(false);
    }, 1000); // Hide poster after 1.5 seconds

    return () => clearTimeout(timer);
  }, []);

  // Build the embed URL with background mode and start time
  const buildBackgroundEmbedUrl = () => {
    const baseUrl = `https://player.vimeo.com/video/${videoId}`;
    const params = new URLSearchParams({
      background: "1", // This enables background mode
      autoplay: "1",
      muted: "1",
      playsinline: "1",
      loop: "1",
      color: "000000", // Black color instead of red
      badge: "0",
      title: "0",
      byline: "0",
      portrait: "0",
      controls: "0",
      responsive: "1",
      vimeo_logo: "0",
      transparent: "0", // Disable transparency
    });

    // Convert seconds to minutes and seconds format for Vimeo
    const formatTime = (seconds: number) => {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m${remainingSeconds}s`;
    };

    const urlWithParams = `${baseUrl}?${params.toString()}`;

    // Add start time using Vimeo's #t= format
    return startTime > 0
      ? `${urlWithParams}#t=${formatTime(startTime)}`
      : urlWithParams;
  };

  return (
    <div
      className={`relative overflow-hidden rounded-lg bg-black ${className}`}
    >
      {/* Poster Image - shown while video is loading */}
      {showPoster && (
        <div className="absolute inset-0 w-full h-full z-20 bg-gray-800">
          <Image
            src="/images/fotograma-1.webp"
            alt="Video poster"
            fill
            className="object-cover"
            priority
            onError={() => console.log("Image failed to load")}
            onLoad={() => console.log("Image loaded successfully")}
          />
        </div>
      )}

      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full bg-black z-10">
        <div className="relative w-full h-full">
          <iframe
            src={buildBackgroundEmbedUrl()}
            title="Free Class Video Background"
            className="absolute border-0 bg-black"
            style={{
              // Position the iframe to cover the container with balanced zoom
              top: "50%",
              left: "50%",
              width: "100%", // Slight scale to ensure coverage without excessive zoom
              height: "100%", // Slight scale to ensure coverage without excessive zoom
              transform: "translate(-50%, -50%)",
              minWidth: "180%",
              minHeight: "100%",
              objectFit: "cover",
              backgroundColor: "#000000", // Force black background
            }}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen={false}
            loading="lazy"
          />
        </div>
      </div>

      {/* Optional overlay for better text readability */}
      <div className="absolute inset-0 bg-black/20 pointer-events-none z-30" />
    </div>
  );
}
